import React, { useState } from "react";
import "./noteart.css";
import { useReveal } from "../../lib/useReveal";


/**
 * Same windows, two ways to count the privacy budget. The toggle redraws the grouping:
 * per window (every square carries its own budget) versus per patient (one budget wraps
 * all of a patient's windows). The patient count and the "about eight" figure are the
 * project's; the totals are just the arithmetic of what is drawn.
 */
const UnitsArt = ({ visual, innerRef, shown }) => {
  const { groups, per } = visual;
  const [mode, setMode] = useState("patient");
  const total = groups * per;
  const text =
    mode === "window"
      ? `${total} separate budgets, though only ${groups} people are behind them`
      : `${groups} budgets, one per patient: the guarantee answers "was this patient in training?"`;

  return (
    <div className="nart nart--units" ref={innerRef} data-shown={shown} data-mode={mode}>
      <div className="nart__toggle" role="group" aria-label="How the privacy budget is counted">
        {[
          ["window", "Per window"],
          ["patient", "Per patient"],
        ].map(([id, label]) => (
          <button key={id} type="button" aria-pressed={mode === id} onClick={() => setMode(id)}>
            {label}
          </button>
        ))}
      </div>

      <div className="nart__units" aria-hidden="true">
        {Array.from({ length: groups }, (_, g) => (
          <div key={g} className="nart__patient">
            <span className="nart__eps">ε</span>
            <div className="nart__grid">
              {Array.from({ length: per }, (_, n) => (
                <span key={n} className="nart__unit" style={{ "--i": g * per + n }} />
              ))}
            </div>
            <span className="nart__pid">patient {String.fromCharCode(65 + g)}</span>
          </div>
        ))}
      </div>

      <p className="nart__legend" aria-hidden="true">
        <i /> one 10-minute window · about {per} per patient
      </p>
      <p className="nart__note" aria-live="polite">{text}</p>
    </div>
  );
};

/** Small evidence graphics for engineering notes. Figures come from the READMEs. */
const NoteArt = ({ visual }) => {
  const [ref, shown] = useReveal({ threshold: 0.3 });

  if (visual.type === "beforeAfter") {
    const { before, after, unit } = visual;
    const max = Math.max(before.value, after.value);
    return (
      <div className="nart" ref={ref} data-shown={shown} role="img" aria-label={`${before.label}: ${before.value} ${unit}. ${after.label}: ${after.value} ${unit}.`}>
        {[
          [before, "nart__bar--bad"],
          [after, "nart__bar--good"],
        ].map(([item, tone], i) => (
          <div key={item.label} className="nart__row" aria-hidden="true">
            <span className="nart__label">{item.label}</span>
            <span className="nart__track">
              <span className={`nart__bar ${tone}`} style={{ "--w": `${(item.value / max) * 100}%`, "--i": i }} />
            </span>
            <span className="nart__value">{item.value}%</span>
          </div>
        ))}
      </div>
    );
  }

  if (visual.type === "overlap") {
    const { percent, a, b } = visual;
    // Comparison set B spans 60–100%; the overlap is `percent`% of B's width.
    const overlapEnd = 60 + (40 * percent) / 100;
    return (
      <div className="nart" ref={ref} data-shown={shown} role="img" aria-label={`About ${percent}% of the comparison set was part of the training fold.`}>
        <div className="nart__lanes" aria-hidden="true">
          {[
            { label: a, bar: [0, overlapEnd], tone: "a" },
            { label: b, bar: [60, 100], tone: "b" },
          ].map((lane) => (
            <div key={lane.tone} className="nart__lane-row">
              <span className="nart__label">{lane.label}</span>
              <span className="nart__lane-track">
                <span
                  className={`nart__lane nart__lane--${lane.tone}`}
                  style={{ "--l": `${lane.bar[0]}%`, "--w": `${lane.bar[1] - lane.bar[0]}%` }}
                />
                <span className="nart__overlap" style={{ "--l": "60%", "--w": `${overlapEnd - 60}%` }} />
              </span>
            </div>
          ))}
        </div>
        <p className="nart__note" aria-hidden="true">≈{percent}% of the comparison set was also in training</p>
      </div>
    );
  }

  if (visual.type === "units") {
    return <UnitsArt visual={visual} innerRef={ref} shown={shown} />;
  }

  if (visual.type === "range") {
    const { items, axis, unit } = visual;
    const pos = (v) => `${((v - axis[0]) / (axis[1] - axis[0])) * 100}%`;
    const summary = items.map((it) => `${it.label}: ${it.mean} ± ${it.sd} ${unit}`).join(". ");
    return (
      <div className="nart" ref={ref} data-shown={shown} role="img" aria-label={`${summary}. The ranges overlap.`}>
        <div className="nart__ranges" aria-hidden="true">
          {items.map((it, i) => (
            <div key={it.label} className="nart__range-row">
              <span className="nart__label">{it.label}</span>
              <span className="nart__range-track">
                <span
                  className={`nart__span${it.highlight ? " nart__span--hi" : ""}`}
                  style={{ "--l": pos(it.mean - it.sd), "--w": `${(2 * it.sd / (axis[1] - axis[0])) * 100}%`, "--i": i }}
                />
                <span className={`nart__mean${it.highlight ? " nart__mean--hi" : ""}`} style={{ "--l": pos(it.mean), "--i": i }} />
              </span>
              <span className="nart__value">{it.mean} ± {it.sd}</span>
            </div>
          ))}
        </div>
        <p className="nart__note" aria-hidden="true">mean wait ± spread over 5 runs: the ranges overlap</p>
      </div>
    );
  }

  return (
    <ul className="nart nart--flags" ref={ref} data-shown={shown} aria-label="Detector results">
      {visual.rows.map((row, i) => (
        <li key={row.label} className="nart__flag" data-tone={row.tone} style={{ "--i": i }}>
          <span>{row.label}</span>
          <strong>{row.result}</strong>
        </li>
      ))}
    </ul>
  );
};

export default NoteArt;
